/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import useHasMounted from '@ion/core/src/hooks/useHasMounted';
import {error} from '@ion/core/src/logger/logger';
import {useIsPhone} from '../../hooks/useMedia';
import ControlRenderer, {Else, If} from '../../render/ControlRenderer';
import IonVisionDialog, {IonVisionDialogProps} from './IonVisionDialog';

// create props definition for the IonVision component...
interface IonVisionProps extends IonVisionDialogProps {
  nativePlayerOnMobile?: boolean;
}

// create styles for the IonVision component...
const useStyles = makeStyles(() => ({
  video : {
    maxWidth : '100%',
    height   : 'auto',
    position : 'absolute',
    opacity  : 0
  }
}));

/**
 * IonVision component that conditionally renders a video player or a dialog.
 * 
 * This component determines whether to display a native video player on mobile
 * devices or to display a dialog based on the `nativePlayerOnMobile` prop and 
 * whether the device is a phone. It also handles entering and exiting fullscreen
 * mode for the video player.
 * 
 * ```typescript
 * 
 *  import IonVision from '@/dls/src/vision/IonVision';
 * 
 *  <IonVision {...props} />
 * 
 * ```
 * 
 * @param nativePlayerOnMobile - Flag to indicate whether to use the native video player on mobile devices.
 * @param rest - Other props inherited from IonVisionDialogProps.
 */
export const IonVision = ({
  nativePlayerOnMobile = false,
  ...rest
}: IonVisionProps) => {
  const classes              = useStyles();
  const isPhone              = useIsPhone();
  const videoRef             = useRef<any>();
  const isMounted            = useHasMounted();
  const {urls, setVideoOpen} = rest;
  const url                  = urls?.[0];

  const [videoElement, setVideoElement] = useState<any>(null);

  const onVideoRefChange = useCallback((node: HTMLVideoElement | null) => {
    videoRef.current = node;
    setVideoElement(node as any);
  }, []);

  useEffect(() => {
    if (isPhone && videoElement && nativePlayerOnMobile) {
      videoElement.src = url;
    }
  }, [isPhone, url, nativePlayerOnMobile, videoElement]);

  useEffect(() => {
    if (nativePlayerOnMobile && videoElement) {
      const cleanupAndClose = () => {
        setVideoOpen(null);
      };

      const handleFullscreenChange = () => {
        if (!document.fullscreenElement) {
          cleanupAndClose();
        }
      };

      const handleSafariFullscreenCheck = () => {
        if (videoElement.webkitDisplayingFullscreen === false) {
          cleanupAndClose();
        }
      };
  
      if (isMounted) {
        setTimeout(() => {
          (async function() {
            if (videoElement) {
            // chrome / firefox or mobile safari api ...
              const api = (videoElement).requestFullscreen ? 'requestFullscreen' : 'webkitEnterFullScreen';
              try {
                await videoElement[api]?.();
                await videoElement?.play?.();
              } catch (err) {
                error('Error requesting fullscreen:', err);
              }
            }
          })();
          document.addEventListener('fullscreenchange', handleFullscreenChange);
          videoElement.addEventListener('webkitendfullscreen', cleanupAndClose);
          videoElement.addEventListener('timeupdate', handleSafariFullscreenCheck);
        }, 1000);
        return () => {
          document?.removeEventListener('fullscreenchange', handleFullscreenChange);
          videoElement?.removeEventListener('webkitendfullscreen', cleanupAndClose);
          videoElement?.removeEventListener('timeupdate', handleSafariFullscreenCheck);
        };
      }
    }
  }, [isMounted, nativePlayerOnMobile, url, videoElement, setVideoOpen]);
  
  return (
    <ControlRenderer condition={nativePlayerOnMobile && isPhone}>
      <If>
        <video ref={onVideoRefChange} className={classes.video} src={url} />
      </If>
      <Else>
        <IonVisionDialog {...rest} />
      </Else>
    </ControlRenderer>
  );
};

export default IonVision;
